import { HierarchyValue } from 'RestAPIs/hierarchy';

export type HierarchyPlanUnit = HierarchyValue & { hierarchy: string[] };

export const createTreeHierarchy = (rows: HierarchyValue[]) => {
  const addHierarchy = (rows: HierarchyValue[]) => {
    const byDynId = new Map(rows.map((r) => [r.brDynId, r])); // O(1) lookup

    const findHierarchy = (
      row: HierarchyValue,
      path: string[] = [],
      visited: Set<number> = new Set()
    ): string[] => {
      // stop if the reference is broken or self-parenting
      if (!row.parentBrDynId || row.parentBrDynId === row.brDynId) return path;

      // cycle-check
      if (visited.has(row.parentBrDynId)) {
        console.warn(
          `[Hierarchy] cycle detected: ${[...visited, row.parentBrDynId].join(
            ' → '
          )}`
        );
        return path; // break the loop
      }

      const parent = byDynId.get(row.parentBrDynId);
      if (!parent) return path; // orphan → stop climbing

      visited.add(row.parentBrDynId); // mark and keep climbing
      path.push(parent.brId.toString());
      return findHierarchy(parent, path, visited);
    };

    return rows.map((row) => {
      const hierarchy = findHierarchy(row).reverse(); // root → child
      return {
        ...row,
        hierarchy: [...hierarchy, row.brId.toString()]
      };
    }) as HierarchyPlanUnit[];
  };

  return addHierarchy(rows);
};

export const getSelectedTreeHierarchy = (
  rows: HierarchyPlanUnit[],
  selectedRowId: string
) => {
  const selectedRow = rows.find((row) => row.brId.toString() === selectedRowId);
  if (!selectedRow) {
    return [];
  }

  // Re-root the subtree on the selected plan unit: every remaining path keeps
  // its full ancestor chain, minus the levels above the selection. Dropping an
  // intermediate level instead would leave the grid grouping rows under an
  // auto-generated, empty parent.
  return rows
    .filter((row) => row.hierarchy?.includes(selectedRowId))
    .map((row) => ({
      ...row,
      hierarchy: row.hierarchy.slice(row.hierarchy.indexOf(selectedRowId))
    }));
};

export const isDirectDescendant = (
  parentRow: HierarchyValue,
  childRow: HierarchyPlanUnit
) => {
  return childRow.parentBrDynId === parentRow.brDynId;
};

export const isDescendant = (
  parentRow: HierarchyValue,
  childRow: HierarchyPlanUnit
) => {
  return childRow.hierarchy?.includes(parentRow.brId?.toString());
};
